// Shared Password Strength Meter Module

const getPasswordChecks = password => ({
  length: password.length >= 8,
  uppercase: /[A-Z]/.test(password),
  lowercase: /[a-z]/.test(password),
  numbers: /\d/.test(password),
  symbols: /[!@#$%^&*()\-_=+[\]{}|\\:;"'<>?,./~`]/.test(password)
});

const validatePassword = password => getPasswordChecks(password);

/**
 * Calculate password strength score (0-4). Length >= 8 is mandatory.
 */
const calculateStrength = password => {
  if (!password) return 0;

  const checks = getPasswordChecks(password);

  // If minimum length requirement is not met, cap at "Very Weak"
  if (!checks.length) {
    return 0;
  }

  // Count optional criteria (uppercase, lowercase, numbers, symbols)
  let optionalScore = 0;
  if (checks.uppercase) optionalScore += 1;
  if (checks.lowercase) optionalScore += 1;
  if (checks.numbers) optionalScore += 1;
  if (checks.symbols) optionalScore += 1;

  // Strength: 0-1 optional = Weak, 2 = Fair, 3 = Good, 4 = Strong
  if (optionalScore <= 1) return 1; // Weak
  if (optionalScore === 2) return 2; // Fair
  if (optionalScore === 3) return 3; // Good
  if (optionalScore === 4) return 4; // Strong

  return 1;
};

const getStrengthLabel = strength => {
  const strengthLabels = ["Very Weak", "Weak", "Fair", "Good", "Strong"];
  return strengthLabels[strength] || "Very Weak";
};
const updateStrengthMeter = (strength, strengthBar, strengthText) => {
  if (!strengthBar || !strengthText) return;
  const strengthLabel = getStrengthLabel(strength);
  strengthBar.setAttribute("data-strength", strength);
  strengthText.setAttribute("data-strength", strength);
  strengthText.textContent = strengthLabel;
  const widthPercentage = strength === 0 ? 10 : (strength / 4) * 100;
  strengthBar.style.width = `${widthPercentage}%`;
};
const updateRequirements = (password, checkElements) => {
  const requirements = validatePassword(password);
  const { lengthCheck, uppercaseCheck, lowercaseCheck, numbersCheck, symbolsCheck } = checkElements;

  // Update each requirement check
  if (lengthCheck) {
    lengthCheck.classList.toggle("completed", requirements.length);
  }
  if (uppercaseCheck) {
    uppercaseCheck.classList.toggle("completed", requirements.uppercase);
  }
  if (lowercaseCheck) {
    lowercaseCheck.classList.toggle("completed", requirements.lowercase);
  }
  if (numbersCheck) {
    numbersCheck.classList.toggle("completed", requirements.numbers);
  }
  if (symbolsCheck) {
    symbolsCheck.classList.toggle("completed", requirements.symbols);
  }
};

const showElements = elements => {
  elements.forEach(element => {
    if (element) {
      element.classList.remove("hidden");
    }
  });
};

const hideElements = elements => {
  elements.forEach(element => {
    if (element) {
      element.classList.add("hidden");
    }
  });
};
const getDomElements = passwordInputSelector => {
  const passwordInput = document.querySelector(passwordInputSelector);
  const requirements = document.querySelector(".js-password-requirements");
  const strengthMeter = document.querySelector(".js-password-strength-meter");
  const strengthBar = document.querySelector(".js-password-strength-bar");
  const strengthText = document.querySelector(".js-password-strength-text");

  const checkElements = {
    lengthCheck: document.querySelector(".js-requirement-length-check"),
    uppercaseCheck: document.querySelector(".js-requirement-uppercase-check"),
    lowercaseCheck: document.querySelector(".js-requirement-lowercase-check"),
    numbersCheck: document.querySelector(".js-requirement-numbers-check"),
    symbolsCheck: document.querySelector(".js-requirement-symbols-check")
  };

  return {
    passwordInput,
    requirements,
    strengthMeter,
    strengthBar,
    strengthText,
    checkElements
  };
};

const createPasswordInputHandler = (elements, debounceTime, debounceTimeDesktop) => {
  const { requirements, strengthMeter, strengthBar, strengthText, checkElements } = elements;

  // Store timeout in closure to scope it per instance
  let timeoutId;

  return event => {
    const password = event.target.value;
    const strength = calculateStrength(password);

    updateStrengthMeter(strength, strengthBar, strengthText);
    updateRequirements(password, checkElements);

    clearTimeout(timeoutId);
    const debounceDelay = window.innerWidth <= 767 ? debounceTime : debounceTimeDesktop;

    timeoutId = setTimeout(() => {
      if (password.length === 0) {
        hideElements([requirements, strengthMeter]);
        return;
      }
      showElements([strengthMeter, requirements]);
    }, debounceDelay);
  };
};

const initPasswordStrengthMeter = (options = {}) => {
  const {
    passwordInputSelector = ".js-password-strength-input",
    debounceTime = 100,
    debounceTimeDesktop = 150
  } = options;

  const elements = getDomElements(passwordInputSelector);
  const { passwordInput } = elements;

  if (!passwordInput) return undefined;

  const handlePasswordInput = createPasswordInputHandler(
    elements,
    debounceTime,
    debounceTimeDesktop
  );

  passwordInput.addEventListener("input", handlePasswordInput);
  passwordInput.addEventListener("focus", handlePasswordInput);
  passwordInput.addEventListener("blur", handlePasswordInput);

  if (passwordInput.value) {
    handlePasswordInput({ target: passwordInput });
  }

  return {
    passwordInput,
    handlePasswordInput,
    validatePassword,
    calculateStrength,
    updateStrengthMeter,
    updateRequirements,
    showElements,
    hideElements
  };
};

export {
  validatePassword,
  calculateStrength,
  getStrengthLabel,
  updateStrengthMeter,
  updateRequirements,
  showElements,
  hideElements
};

export default initPasswordStrengthMeter;
